import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, Tag, Newspaper } from "lucide-react";
import { usePageSeo, BASE_URL } from "@/hooks/useSeo";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import DOMPurify from "dompurify";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NewsletterDetailPage() {
  const { nlId } = useParams();
  const [newsletter, setNewsletter] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/newsletter-archive/${nlId}`)
      .then(r => setNewsletter(r.data))
      .catch(() => setNewsletter(null))
      .finally(() => setLoading(false));
  }, [nlId]);

  usePageSeo({
    title: newsletter ? `${newsletter.title} | LAB336 Newsletter` : "Newsletter | LAB336",
    description: newsletter?.summary || "Newsletter-Ausgabe von LAB336.",
    canonical: `${BASE_URL}/newsletter/${nlId}`,
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-lab-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!newsletter) {
    return (
      <div className="min-h-screen bg-white" data-testid="newsletter-not-found">
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
          <h1 className="font-playfair text-2xl font-semibold text-lab-primary mb-4">Newsletter nicht gefunden</h1>
          <p className="text-lab-text-muted mb-8">Diese Ausgabe existiert nicht oder wurde nicht veröffentlicht.</p>
          <Link to="/newsletter" className="text-lab-accent hover:underline">Zum Newsletter-Archiv</Link>
        </div>
      </div>
    );
  }

  const isHtml = newsletter.content && (newsletter.content.includes('<p>') || newsletter.content.includes('<div>') || newsletter.content.includes('<h'));

  return (
    <div className="min-h-screen bg-white" data-testid="newsletter-detail-page">
      <header className="bg-lab-bg-dark">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 py-12">
          <Link to="/newsletter" className="inline-flex items-center gap-2 text-gray-400 hover:text-lab-accent transition-colors text-sm mb-8" data-testid="back-to-newsletter">
            <ArrowLeft className="w-4 h-4" /> Newsletter-Archiv
          </Link>
          <div className="flex items-center gap-3 text-sm text-gray-400 mb-4">
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {new Date(newsletter.published_at).toLocaleDateString("de-CH", { day: "numeric", month: "long", year: "numeric" })}
            </span>
            <span className="flex items-center gap-1">
              <Tag className="w-4 h-4" />{newsletter.category}
            </span>
          </div>
          <h1 className="font-playfair text-2xl lg:text-4xl font-semibold text-white" data-testid="newsletter-detail-title">
            {newsletter.title}
          </h1>
          {newsletter.summary && (
            <p className="text-gray-400 mt-4 text-base lg:text-lg leading-relaxed">{newsletter.summary}</p>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 lg:px-8 py-16">
        <motion.article
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="prose prose-lg max-w-none text-lab-text leading-relaxed
            prose-headings:font-playfair prose-headings:text-lab-primary prose-headings:font-semibold
            prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4 prose-h2:border-b prose-h2:border-lab-border prose-h2:pb-3
            prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
            prose-p:mb-4 prose-p:leading-relaxed
            prose-hr:my-8 prose-hr:border-lab-border
            prose-strong:text-lab-primary prose-strong:font-semibold
            prose-ul:my-4 prose-ul:space-y-2 prose-li:text-lab-text
            prose-a:text-lab-accent prose-a:underline"
          data-testid="newsletter-detail-content"
        >
          {isHtml ? (
            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(newsletter.content) }} />
          ) : (
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{newsletter.content}</ReactMarkdown>
          )}
        </motion.article>

        <div className="mt-16 pt-8 border-t border-lab-border flex items-center justify-between">
          <Link to="/newsletter" className="text-lab-accent hover:underline text-sm inline-flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Alle Ausgaben
          </Link>
          <div className="flex items-center gap-2 text-sm text-lab-text-muted">
            <Newspaper className="w-4 h-4" />
            <span>{newsletter.category}</span>
          </div>
        </div>
      </main>

      <footer className="bg-lab-bg-dark border-t border-white/10">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 py-8 flex items-center justify-between">
          <p className="text-gray-500 text-sm">&copy; {new Date().getFullYear()} LAB 336. Alle Rechte vorbehalten.</p>
          <Link to="/" className="text-gray-400 hover:text-lab-accent text-sm transition-colors">Startseite</Link>
        </div>
      </footer>
    </div>
  );
}
